import { useEffect, useState, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Calendar, Filter, Search, Radio, Users, Clock, ChevronDown, Map, List } from 'lucide-react';
import DOMPurify from 'dompurify';

import { useAppStore } from '../stores/appStore';
import { useEvents, useEventFilters } from '../hooks/useEvents';
import { useLiveEvents } from '../hooks/useEvents';
import { EventCategory, EventStatus } from '../types';
import { categoryService, Category } from '../services/category.service';

import EventCard from '../components/Events/EventCard';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import EmptyState from '../components/Common/EmptyState';
import { EventDetailModal } from '../components/EventDetailModal';
import { LucideIcon } from '../utils/iconHelper';
import InteractiveMap from '../components/Map/InteractiveMap';

// Sanitized embed component to prevent XSS
const SafeEmbed = ({ html }: { html: string }) => {
  const sanitized = useMemo(() => {
    const clean = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['iframe'],
      ALLOWED_ATTR: ['src', 'width', 'height', 'allow', 'allowfullscreen', 'style', 'frameborder'],
    });
    return clean.replace(/<iframe/g, '<iframe style="width: 100%; height: 100%; border: none; position: absolute; top: 0; left: 0;"');
  }, [html]);

  return <div className="w-full h-full" dangerouslySetInnerHTML={{ __html: sanitized }} />;
};

const HomePage = () => {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState<'all' | EventStatus>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [registeredEvents, setRegisteredEvents] = useState<Set<string>>(new Set());
  const [eventReminders, setEventReminders] = useState<Set<string>>(new Set());
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [streamSettings, setStreamSettings] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    selectedDate,
    setCurrentEvent,
    filteredEvents,
    isLoading,
  } = useAppStore();

  const { events, refetch } = useEvents({ date: selectedDate });
  const { liveEvents } = useLiveEvents();
  const { filterByStatus, filterByCategory, getActiveEvents, getUpcomingEvents } = useEventFilters(events);

  // Load categories on component mount
  useEffect(() => {
    const loadCategories = async () => {
      setCategoriesLoading(true);
      try {
        const fetchedCategories = await categoryService.getActiveCategories();
        setCategories(fetchedCategories);
      } catch (error) {
        console.error('Error loading categories:', error);
        setCategories([]);
      } finally {
        setCategoriesLoading(false);
      }
    };
    
    loadCategories();
  }, []);

  // Load stream settings on component mount
  useEffect(() => {
    const loadStreamSettings = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || '/api';
        const response = await fetch(`${apiUrl}/settings/stream/public`);
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setStreamSettings(data.data);
          }
        }
      } catch (error) {
        console.error('Error loading stream settings:', error);
      }
    };
    
    loadStreamSettings();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter events based on active filters
  const getFilteredEvents = () => {
    let filtered = filteredEvents;

    // Status filter
    if (activeFilter !== 'all') {
      filtered = filterByStatus(activeFilter);
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filterByCategory(categoryFilter);
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(event =>
        event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const filteredEventList = getFilteredEvents();
  const activeEvents = getActiveEvents();
  const upcomingEvents = getUpcomingEvents();

  const handleEventClick = (event: any) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const handleRegister = (eventId: string) => {
    setRegisteredEvents(prev => new Set([...prev, eventId]));
    // TODO: Call API to register for event
    console.log('Registering for event:', eventId);
  };

  const handleSetReminder = (eventId: string) => {
    setEventReminders(prev => new Set([...prev, eventId]));
    // TODO: Call API to set reminder
    console.log('Setting reminder for event:', eventId);
  };

  const handleRefresh = async () => {
    await refetch();
  };

  // Stats
  const stats = [
    {
      label: t('events.active'),
      value: activeEvents.length,
      icon: Radio,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      label: t('events.upcoming'),
      value: upcomingEvents.length,
      icon: Clock,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      label: 'Total',
      value: filteredEvents.length,
      icon: Calendar,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
  ];

  const filterButtons = [
    { key: 'all', label: 'Todos' },
    { key: 'active', label: t('events.active') },
    { key: 'upcoming', label: t('events.upcoming') },
    { key: 'finished', label: t('events.finished') },
  ] as const;

  // Generate dynamic category buttons from backend data
  const categoryButtons = [
    { key: 'all', label: 'Todas', color: '#667eea', icon: 'layers' },
    ...categories.map(category => ({
      key: category.name,
      label: category.name,
      color: category.color || '#667eea',
      icon: category.icon || 'tag'
    }))
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-manga text-white py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          {/* Logo del evento si está disponible */}
          {streamSettings?.eventLogoUrl && (
            <motion.div
              className="mb-4"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            >
              <img
                src={streamSettings.eventLogoUrl}
                alt="Logo del evento"
                className="max-h-24 md:max-h-32 mx-auto object-contain drop-shadow-lg"
              />
            </motion.div>
          )}
          
          <motion.h1
            className="text-2xl md:text-3xl font-bold mb-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: streamSettings?.eventLogoUrl ? 0.2 : 0 }}
          >
            Bienvenidos
          </motion.h1>
          
          <motion.p
            className="text-lg opacity-90 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: streamSettings?.eventLogoUrl ? 0.4 : 0.2 }}
          >
            Abajo toda la programación de hoy, {format(selectedDate, 'd MMMM', { locale: es })}
          </motion.p>

          {/* Live Events Banner */}
          <AnimatePresence>
            {liveEvents.length > 0 && (
              <motion.div
                className="bg-white bg-opacity-20 backdrop-blur-sm rounded-2xl p-4 mb-4"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <Radio className="w-5 h-5 animate-pulse" />
                  <span className="font-semibold">¡{liveEvents.length} evento(s) en vivo!</span>
                </div>
                <div className="flex flex-wrap gap-2 justify-center">
                  {liveEvents.map((event) => (
                    <button
                      key={event.id}
                      className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm hover:bg-opacity-30 transition-colors"
                      onClick={() => handleEventClick(event)}
                    >
                      {event.title}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Stream/Logo Section */}
          {streamSettings && (
            <motion.div
              className="mt-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {streamSettings.isActive && streamSettings.embedCode ? (
                /* Stream activo */
                <div 
                  className="relative bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-4 overflow-hidden"
                  style={{
                    background: `linear-gradient(135deg, ${streamSettings.backgroundColor || '#667eea'}33, ${streamSettings.backgroundColor || '#667eea'}66)`
                  }}
                >
                  <div className="relative aspect-video w-full max-w-3xl mx-auto rounded-xl overflow-hidden shadow-lg">
                    <SafeEmbed html={streamSettings.embedCode || ''} />
                  </div>
                  <div className="mt-3 text-center">
                    <div className="flex items-center justify-center space-x-2 text-white">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-medium">EN VIVO</span>
                    </div>
                  </div>
                </div>
              ) : streamSettings.eventLogoUrl ? (
                /* Logo del evento cuando no hay stream */
                <div 
                  className="relative bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-8"
                  style={{
                    background: `linear-gradient(135deg, ${streamSettings.backgroundColor || '#667eea'}22, ${streamSettings.backgroundColor || '#667eea'}44)`
                  }}
                >
                  <div className="text-center">
                    <div className="max-w-md mx-auto">
                      <img
                        src={streamSettings.eventLogoUrl}
                        alt="Logo del evento"
                        className="max-w-full max-h-48 mx-auto object-contain rounded-lg shadow-soft"
                        style={{
                          filter: `drop-shadow(0 8px 16px rgba(0,0,0,0.1))`
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : null}
            </motion.div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-4xl mx-auto px-4 -mt-8 relative z-10">
        <div className="grid grid-cols-3 gap-4 mb-6">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              className="bg-white rounded-2xl p-4 shadow-soft text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className={`w-12 h-12 ${stat.bgColor} rounded-full flex items-center justify-center mx-auto mb-2`}>
                <stat.icon className={`w-6 h-6 ${stat.color}`} />
              </div>
              <div className="text-2xl font-bold text-gray-800">{stat.value}</div>
              <div className="text-sm text-gray-600">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Filters and Search */}
      <div className="max-w-4xl mx-auto px-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar eventos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent shadow-sm"
            />
          </div>

          {/* View Mode Toggle */}
          <div className="flex bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center space-x-1.5 px-4 py-3 transition-colors ${
                viewMode === 'list'
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              title="Vista lista"
            >
              <List className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`flex items-center space-x-1.5 px-4 py-3 transition-colors ${
                viewMode === 'map'
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              title="Vista mapa"
            >
              <Map className="w-5 h-5" />
            </button>
          </div>

          {/* Category Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
              className="w-full md:w-auto px-5 py-3 bg-white border border-gray-200 rounded-xl flex items-center justify-between space-x-3 hover:shadow-md transition-shadow duration-200 shadow-sm min-w-[200px]"
              disabled={categoriesLoading}
            >
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5 text-gray-500" />
                <span className="font-medium text-gray-700">
                  {categoriesLoading ? (
                    'Cargando...'
                  ) : categoryFilter === 'all' ? (
                    'Todas las categorías'
                  ) : (
                    <span className="flex items-center space-x-1">
                      <LucideIcon name={categoryButtons.find(c => c.key === categoryFilter)?.icon || 'tag'} size={16} className="text-gray-500" />
                      <span>{categoryButtons.find(c => c.key === categoryFilter)?.label}</span>
                    </span>
                  )}
                </span>
              </div>
              <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${showCategoryDropdown ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            <AnimatePresence>
              {showCategoryDropdown && !categoriesLoading && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute z-50 mt-2 w-full md:w-72 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden"
                >
                  <div className="max-h-80 overflow-y-auto">
                    {categoryButtons.map((button) => (
                      <button
                        key={button.key}
                        onClick={() => {
                          setCategoryFilter(button.key);
                          setShowCategoryDropdown(false);
                        }}
                        className={`w-full px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors duration-150 ${
                          categoryFilter === button.key ? 'bg-gradient-manga text-white' : 'text-gray-700'
                        }`}
                        style={{
                          backgroundColor: categoryFilter === button.key ? button.color : undefined
                        }}
                      >
                        <LucideIcon name={button.icon} size={18} className={categoryFilter === button.key ? 'text-white' : 'text-gray-500'} />
                        <span className="font-medium">{button.label}</span>
                        {categoryFilter === button.key && (
                          <motion.div
                            layoutId="selected-category"
                            className="ml-auto"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                          >
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          </motion.div>
                        )}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Events List / Map View */}
      <div className="max-w-4xl mx-auto px-4 pb-8">
        {viewMode === 'map' ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl shadow-soft p-4"
          >
            <InteractiveMap />
          </motion.div>
        ) : (
          <AnimatePresence mode="wait">
            {isLoading ? (
              <LoadingSpinner />
            ) : filteredEventList.length === 0 ? (
              <EmptyState
                title={searchTerm ? 'No se encontraron eventos' : t('events.noEventsToday')}
                description={
                  searchTerm
                    ? 'Intenta con otros términos de búsqueda'
                    : 'No hay eventos programados para esta fecha'
                }
                actionLabel="Actualizar"
                onAction={handleRefresh}
              />
            ) : (
              <motion.div
                className="space-y-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                {filteredEventList.map((event, index) => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <EventCard
                      event={event}
                      onClick={handleEventClick}
                      showCategory
                      showProgress
                    />
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
      
      {/* Event Detail Modal */}
      <EventDetailModal
        event={selectedEvent}
        isOpen={showEventModal}
        onClose={() => {
          setShowEventModal(false);
          setSelectedEvent(null);
        }}
        onRegister={handleRegister}
        onSetReminder={handleSetReminder}
        isRegistered={selectedEvent ? registeredEvents.has(selectedEvent.id) : false}
        hasReminder={selectedEvent ? eventReminders.has(selectedEvent.id) : false}
      />
    </div>
  );
};

export default HomePage;